import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { shellApi } from '@/lib/api/endpoints';
import type { ShellPayload } from '@/lib/api/types';

export interface ShellContextValue {
  shell: ShellPayload | null;
  isLoading: boolean;
  error: string | null;
  reload: () => void;
  isDrawerOpen: boolean;
  openDrawer: () => void;
  closeDrawer: () => void;
  toggleDrawer: () => void;
}

export const ShellContext = createContext<ShellContextValue | null>(null);

export const ShellProvider = ({ children }: { children: ReactNode }) => {
  const { token } = useAuth();
  const [shell, setShell] = useState<ShellPayload | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    shellApi
      .read()
      .then((payload) => {
        if (cancelled) return;
        setShell(payload);
        setError(null);
      })
      .catch((reason: unknown) => {
        if (cancelled) return;
        setError(reason instanceof Error ? reason.message : 'Не удалось загрузить оболочку');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nonce, token]);

  useEffect(() => {
    if (!isDrawerOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsDrawerOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isDrawerOpen]);

  const reload = useCallback(() => setNonce((value) => value + 1), []);
  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);
  const toggleDrawer = useCallback(() => setIsDrawerOpen((open) => !open), []);

  const value = useMemo<ShellContextValue>(
    () => ({
      shell,
      isLoading,
      error,
      reload,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      toggleDrawer,
    }),
    [closeDrawer, error, isDrawerOpen, isLoading, openDrawer, reload, shell, toggleDrawer],
  );

  return <ShellContext.Provider value={value}>{children}</ShellContext.Provider>;
};
